<template>
  <div class="SavedPostsModule">
    <!-- Chat context header -->
    <div class="SavedPostsModule__chat-context">
      <ImageLoader v-if="chat.image" :file="chat.image" small class="SavedPostsModule__chat-thumb" />
      <div>
        <div class="SavedPostsModule__chat-name">{{ chat.name }}</div>
        <router-link :to="`/chats/${chatId}`" class="SavedPostsModule__back-link">
          {{ $t('forum.saved_page.back_to_threads') }}
        </router-link>
      </div>
    </div>

    <!-- Page head -->
    <div class="SavedPostsModule__head">
      <h1 class="SavedPostsModule__title">{{ $t('forum.saved_page.title') }}</h1>
      <span class="SavedPostsModule__count">{{ $tc('common_amount_results', fetcher.totalRecords) }}</span>
    </div>

    <!-- Toolbar -->
    <div class="SavedPostsModule__toolbar">
      <LLSearchBar
        v-model="searchValue"
        :placeholder="$t('forum.saved_page.search_placeholder')"
        class="SavedPostsModule__search"
        @search="onSearch"
      />
      <div class="SavedPostsModule__filter">
        <LLInputSelect
          v-model="selectedThreadId"
          :values="threadFilterValues"
          :key-fn="threadKeyFn"
          :caption-fn="threadCaptionFn"
          :reduce="threadReduceFn"
          class="SavedPostsModule__select"
          @input="onFilterChange"
        />
      </div>
      <div class="SavedPostsModule__sort">
        <LLInputSelect
          v-model="sortType"
          :values="sortValues"
          :key-fn="sortKeyFn"
          :caption-fn="sortCaptionFn"
          :reduce="sortReduceFn"
          class="SavedPostsModule__select"
          @input="onSortChange"
        />
      </div>
    </div>

    <!-- Loading state -->
    <div v-if="fetcher.isFetching" class="SavedPostsModule__loading">
      <LLLoader />
    </div>

    <!-- Error state -->
    <div v-else-if="fetcher.error" class="SavedPostsModule__error">
      <LLLoaderError @retry="fetch">
        {{ fetcher.error }}
      </LLLoaderError>
    </div>

    <!-- Results list -->
    <div v-else-if="visibleResults.length" class="SavedPostsModule__list">
      <SavedPostCard
        v-for="(item, idx) in visibleResults"
        :key="item.type + '-' + (item.savedPostId || item.id || idx)"
        :item="item"
        :chat-id="chatId"
        class="SavedPostsModule__card"
        @removed="onRemoved"
        @restored="onRestored"
        @dismissed="onDismissed"
      />
    </div>

    <!-- Empty state -->
    <div v-else class="SavedPostsModule__empty">
      <SavedPostsIcon class="SavedPostsModule__empty-icon" />
      <h2 class="SavedPostsModule__empty-title">{{ $t('forum.saved_page.nothing_saved_title') }}</h2>
      <p class="SavedPostsModule__empty-text">{{ $t('forum.saved_page.nothing_saved_text') }}</p>
    </div>

    <!-- Pagination -->
    <div v-if="fetcher.totalRecords > perPage" class="SavedPostsModule__pagination">
      <LLPagination
        :current="fetcher.page - 1"
        :total="fetcher.totalRecords"
        :per-page="perPage"
        :loading="fetcher.isFetching"
        @goToPage="onPageChange"
      />
    </div>
  </div>
</template>

<script>
import LLSearchBar from '@/components/common/LLSearchBar'
import LLLoader from '@/components/common/LLLoader'
import LLLoaderError from '@/components/common/LLLoaderError'
import LLPagination from '@/components/common/LLPagination'
import LLInputSelect from '@/components/common/LLInput/LLInputSelect.vue'
import ImageLoader from '@/components/common/ImageLoader/ImageLoader.vue'
import SavedPostsIcon from '@/assets/icons/SavedPostsIcon.vue'
import SavedPostCard from './SavedPostCard.vue'
import { ForumChatModel } from '@/models/forum/forumChat'
import { Fetcher } from '@/helpers/fetcher'

export default {
  name: 'SavedPostsModule',
  components: {
    LLSearchBar,
    LLLoader,
    LLLoaderError,
    LLPagination,
    LLInputSelect,
    ImageLoader,
    SavedPostsIcon,
    SavedPostCard
  },
  props: {
    chat: { type: [ForumChatModel, null], default: null },
    threads: { type: Array, default: () => [] }
  },
  data() {
    return {
      searchValue: '',
      selectedThreadId: '__all__',
      sortType: 'newest',
      perPage: 20,
      fetcher: new Fetcher({ perPage: 20 }),
      lastFetchedParams: null
    }
  },
  computed: {
    chatId() {
      return this.chat?.id
    },
    threadFilterValues() {
      return [
        { label: this.$t('forum.saved_page.all_threads'), value: '__all__' },
        ...this.threads.map((t) => ({ label: t.title, value: t.id }))
      ]
    },
    sortValues() {
      return [
        { label: this.$t('forum.saved_page.sort_newest'), value: 'newest' },
        { label: this.$t('forum.saved_page.sort_oldest'), value: 'oldest' }
      ]
    },
    visibleResults() {
      return this.fetcher.data.filter((r) => !r._removed)
    }
  },
  created() {
    this.fetch()
  },
  methods: {
    threadKeyFn(v) {
      return v.value
    },
    threadCaptionFn(v) {
      return v.label
    },
    threadReduceFn(v) {
      return v.value
    },
    sortKeyFn(v) {
      return v.value
    },
    sortCaptionFn(v) {
      return v.label
    },
    sortReduceFn(v) {
      return v.value
    },
    onSearch() {
      if (this.searchValue.length > 0 && this.searchValue.length < 3) {
        this.$toast.error(this.$t('search-page_keyword_too_short'))
        return
      }
      this.resetAndFetch()
    },
    onFilterChange() {
      if (this.hasParamsChanged()) {
        this.resetAndFetch()
      }
    },
    onSortChange() {
      if (this.hasParamsChanged()) {
        this.resetAndFetch()
      }
    },
    onPageChange(page) {
      this.fetcher.resetData()
      this.fetcher.reset()
      this.fetcher.page = page + 1
      this.fetch()
    },
    resetAndFetch() {
      this.fetcher.resetData()
      this.fetcher.reset()
      this.fetcher._isFetching = true
      this.debouncedFetch()
    },
    // eslint-disable-next-line
    debouncedFetch: _.debounce(function () {
      this.fetch()
    }, 100),
    onRemoved() {
      this.fetcher._totalRecords = Math.max(0, this.fetcher._totalRecords - 1)
    },
    onRestored() {
      this.fetcher._totalRecords += 1
    },
    onDismissed({ item }) {
      const idx = this.fetcher.data.findIndex(
        (r) => r.type === item.type && (r.savedPostId || r.id) === (item.savedPostId || item.id)
      )
      if (idx >= 0) {
        this.$set(this.fetcher.data[idx], '_removed', true)
      }
    },
    fetch() {
      const params = {
        chatId: this.chatId,
        search: this.searchValue || undefined,
        threadId:
          this.selectedThreadId && this.selectedThreadId !== '__all__' ? [this.selectedThreadId] : undefined,
        sort: this.sortType
      }

      this.lastFetchedParams = JSON.stringify(params)

      this.fetcher.fetch(async ({ page, perPage }) => {
        const { total, data } = await this.$api.forum.savedPosts.getSavedPosts({
          ...params,
          page: page ? page - 1 : 0,
          perPage
        })
        return { totalRecords: total, data: (data || []).map((r) => ({ ...r, _removed: false })) }
      })
    },
    hasParamsChanged() {
      const current = JSON.stringify({
        chatId: this.chatId,
        search: this.searchValue || undefined,
        threadId:
          this.selectedThreadId && this.selectedThreadId !== '__all__' ? [this.selectedThreadId] : undefined,
        sort: this.sortType
      })
      return current !== this.lastFetchedParams
    }
  }
}
</script>

<style lang="scss" scoped>
.SavedPostsModule {
  max-width: 860px;
  margin: 0 auto;

  &__loading {
    @apply flex justify-center py-16;
  }

  &__error {
    @apply py-16;
  }

  &__chat-context {
    @apply flex items-center gap-3 mb-7;
  }
  &__chat-thumb {
    @apply w-11 h-11 flex-shrink-0 object-cover;
  }
  &__chat-name {
    @apply font-bold;
  }
  &__back-link {
    @apply text-caption-02 text-neutral-01-400;
  }

  &__head {
    @apply flex items-baseline gap-2.5 mb-1.5;
  }
  &__title {
    @apply text-2xl font-normal;
  }
  &__count {
    @apply text-body-03 text-neutral-01-400;
  }

  &__toolbar {
    @apply flex gap-2.5 items-center flex-wrap mb-7;
  }
  &__search {
    flex: 1 1 260px;
    width: auto !important;
  }
  &__filter {
    @apply flex items-center gap-1.5 text-caption-02 flex-shrink-0;
  }
  &__sort {
    @apply flex-shrink-0;
  }
  &__select {
    min-width: 170px;
  }

  &__list {
    @apply transition-opacity duration-200;
  }
  &__card {
    @apply mt-3;
    &:first-child {
      @apply mt-0;
    }
  }

  &__empty {
    @apply flex flex-col items-center py-16 px-5 text-center;
  }
  &__empty-icon {
    @apply text-neutral-01-200 mb-4;
    width: 44px;
    height: 44px;
  }
  &__empty-title {
    @apply text-subheading-01 mb-1.5;
  }
  &__empty-text {
    @apply text-body-03 text-neutral-01-400 max-w-sm;
  }

  &__pagination {
    @apply flex justify-center mt-6;
  }
}
</style>
